import { formatCurrency } from 'common/FormatUtilities';
import { getDeliveryDateCutoff } from 'common/OrderDateRangeUtilities';
import { DateTime } from 'luxon';
import React from 'react';
import {
  DishFirstFoodItemPreviewFragment,
  DishFirstFoodItemsSegmentFragment,
  DishFirstPublicShefFragment,
  DishFirstSegmentFilter,
} from 'src/gqlReactTypings.generated.d';
import { Chip } from 'src/shared/design-system/Chip';
import { Clock } from 'src/shared/design-system/Icon';
import { switchProp } from 'src/shared/styled-utils/switchProp';
import styled, { css } from 'styled-components';
import { match, P } from 'ts-pattern';
import { Colors, Font } from 'web-common/src/shared/styles';

type ChipType =
  | 'popular'
  | 'promo'
  | 'new'
  | 'getItTomorrow'
  | 'trialShef'
  | 'neutral'
  | 'perfectMatch'
  | 'goodAlternative'
  | 'perfectMatchTitle';

export const StyledChip = styled(Chip)<{ type: ChipType }>`
  font-size: ${Font.Size.XXS};
  font-weight: ${Font.Weight.SEMI_BOLD};
  user-select: none;

  ${switchProp('type', {
    popular: css`
      color: ${Colors.GRAY_DARK_30};
      background-color: ${Colors.STRAWBERRY_50};
    `,
    promo: css`
      color: ${Colors.GRAY_DARK_30};
      background-color: ${Colors.MINT_300};
    `,
    new: css`
      color: ${Colors.GRAY_DARK_30};
      background-color: ${Colors.JACKFRUIT};
    `,
    getItTomorrow: css`
      color: ${Colors.WHITE};
      background-color: ${Colors.ELDERBERRY_DARK_20};
    `,
    trialShef: css`
      color: ${Colors.WHITE};
      background-color: ${Colors.STRAWBERRY_600};
    `,
    neutral: css`
      color: ${Colors.GRAY_DARK_30};
      background-color: ${Colors.WHITE};
    `,
    goodAlternative: css`
      color: #7b0f37;
      background-color: ${Colors.WHITE};
    `,
    perfectMatch: css`
      color: ${Colors.KOHLRABI_DARK_10};
      background-color: ${Colors.WHITE};
    `,
    perfectMatchTitle: css`
      color: ${Colors.KOHLRABI_DARK_10};
      background-color: ${Colors.KOHLRABI_LITE_40};
    `,
  })}
`;

export const orderByTime = (cutoffMsBeforeDeliveryDate: number): string => {
  const cutoff = getDeliveryDateCutoff(DateTime.now().startOf('day').plus({ day: 1 }), cutoffMsBeforeDeliveryDate);
  return cutoff.toFormat(cutoff.minute === 0 ? 'ha' : 'h:mm a').toLowerCase();
};

interface IconProps {
  icon?: React.ReactNode;
  className?: string;
}

const Icon: React.FC<IconProps> = (props) => <div className={props.className}>{props.icon}</div>;

interface IProps {
  foodItem?: DishFirstFoodItemPreviewFragment;
  shef?: DishFirstPublicShefFragment;
  segment?: DishFirstFoodItemsSegmentFragment;
}

export const EnticementChip: React.FC<IProps> = ({ foodItem, shef, segment }) =>
  // Hide on collections
  match({ segmentId: segment?.id, foodItem, shef: shef ?? foodItem?.shef })
    .with({ segmentId: DishFirstSegmentFilter.All }, () => null)
    .with({ foodItem: { lastPurchasedAt: P.not(P.nullish) } }, ({ foodItem }) => {
      const [year, month, day] = foodItem.lastPurchasedAt.split('-');
      return <StyledChip type='neutral' text={`Ordered ${month}/${day}/${year}`} />;
    })
    .with({ shef: { shefProfile: { explorePagePromoCode: P.not(P.nullish) } } }, ({ shef }) => {
      const { isPercentage, value } = shef.shefProfile.explorePagePromoCode;
      const valueString = isPercentage ? `${value}%` : formatCurrency(value, true);
      return <StyledChip type='promo' text={`${valueString} Off`} />;
    })
    .with({ segmentId: DishFirstSegmentFilter.NextDay }, () => {
      const contentStr = `Order by ${orderByTime(foodItem?.shef?.shefProfile?.cutoffMsBeforeDeliveryDate ?? 0)}`;
      return (
        <StyledChip
          type='getItTomorrow'
          text={contentStr}
          icon={<Icon className='-mt-px mr-1 [&>svg]:size-[9px]' icon={<Clock />} />}
        />
      );
    })
    .with({ segmentId: DishFirstSegmentFilter.Favorites }, () => <StyledChip type='popular' text='Popular' />)
    .with({ segmentId: DishFirstSegmentFilter.New }, () => <StyledChip type='new' text='New' />)
    .with({ shef: { shefProfile: { isTrialShef: true } } }, () => <StyledChip type='trialShef' text='Trial Shef' />)
    .with({ foodItem: { isPopular: true } }, () => <StyledChip type='popular' text='Popular' />)
    .otherwise(() => null);
